import queryString from 'query-string';
import {useMemo} from 'react';

import {
  OrganizationSubscriptionStatus,
  useOpenMatchingOrganizationsQuery,
} from '../generated/graphql';
import {useViewer} from '../state/viewer/hooks';
import {getAuthRedirect} from '../util/auth';
import {useUserEmails} from '../util/useUserEmails';

interface JoinTeamsResult {
  showTeamsContent: boolean;
  viewerEmail: string | undefined;
}

export function useShowJoinTeamsContent(location: string): JoinTeamsResult {
  const viewer = useViewer();
  const {verifiedCorporateEmails} = useUserEmails();

  const authRedirect = getAuthRedirect();
  const {query} = queryString.parseUrl(authRedirect);

  const hasVerifiedCorporateEmail =
    verifiedCorporateEmails != null && verifiedCorporateEmails.length > 0;

  const viewerEmailDomain = hasVerifiedCorporateEmail
    ? verifiedCorporateEmails[0]?.emailAddress.split('@')[1]
    : undefined;

  const {data} = useOpenMatchingOrganizationsQuery({
    variables: {emailDomain: viewerEmailDomain},
    skip: !hasVerifiedCorporateEmail,
  });

  const userTeamIDs = useMemo(
    () => new Set(viewer?.teams?.edges?.map(({node}) => node.id) ?? []),
    [viewer?.teams?.edges]
  );

  const hasAvailableMatchingTeams = useMemo(() => {
    const orgs = data?.viewer?.openMatchingOrganizations;
    if (!orgs) {
      return false;
    }
    const enabledOrgs = orgs.filter(org =>
      org.subscriptions.some(
        sub => sub.status === OrganizationSubscriptionStatus.Enabled
      )
    );
    return enabledOrgs.some(({teams}) =>
      teams.some(
        team =>
          !userTeamIDs.has(team.id) &&
          team.settings.openToMatchingOrgEmailDomainUsers
      )
    );
  }, [data, userTeamIDs]);

  const showJoinTeamsContent =
    hasVerifiedCorporateEmail && hasAvailableMatchingTeams;

  const justAddedVerifiedCorporateEmail = query.addEmail === `true`;

  if (justAddedVerifiedCorporateEmail) {
    window.analytics?.track('Added work email', {
      entityName: viewer?.name,
      location,
    });
  }
  return {
    showTeamsContent: showJoinTeamsContent,
    viewerEmail:
      verifiedCorporateEmails && verifiedCorporateEmails[0]?.emailAddress,
  };
}
